import { Archive, CheckCircle2, Plus, RefreshCw, RotateCcw, Search, Trash2 } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';
import {
  archiveBotCommand,
  createBotCommand,
  deleteBotCommand,
  loadBotWithSounds,
  restoreBotCommandForSetup,
  updateBotCommand,
  type BotCommand,
  type BotCommandAction,
  type BotPermission,
  type SoundChoice,
  type TwitchBotSnapshot,
} from './bot-api';
import './workspace.css';

type Draft = {
  response: string;
  aliases: string;
  cooldown: string;
  permission: BotPermission;
};

const permissionLabels: Record<BotPermission, string> = {
  everyone: 'Everyone',
  moderator: 'Moderators',
  broadcaster: 'Broadcaster only',
};

function commandDraft(command: BotCommand): Draft {
  return {
    response: command.responseTemplate ?? '',
    aliases: command.aliases.join(', '),
    cooldown: String(command.cooldownSeconds),
    permission: command.permission,
  };
}

function archived(command: BotCommand): boolean {
  return command.internalState === 'retire_review' || command.internalState === 'already_replaced';
}

function unsupported(command: BotCommand): boolean {
  return command.internalState === 'legacy_builtin_review';
}

function validCooldown(value: string): number | null {
  if (!/^\d+$/.test(value.trim())) return null;
  const seconds = Number(value);
  return Number.isInteger(seconds) && seconds >= 0 && seconds <= 3600 ? seconds : null;
}

function friendlyReason(reason: string): string {
  return reason
    .replace(/legacy/gi, 'previous')
    .replace(/native/gi, 'FFSControl')
    .replace(/Streamer\.bot/gi, 'previous automation');
}

function actionText(action: BotCommandAction): string {
  if (action.type === 'play_sound') return `Play sound · ${action.value}`;
  if (action.type === 'random_sound') return `Random sound · ${action.values.length} choices`;
  if (action.type === 'append_file') return `Write to output file · ${action.value}`;
  return `Random response list · ${action.value}`;
}

export function CommandsPage() {
  const [bot, setBot] = useState<TwitchBotSnapshot | null>(null);
  const [sounds, setSounds] = useState<SoundChoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'enabled' | 'disabled' | 'setup' | 'archived'>('all');
  const [permissionFilter, setPermissionFilter] = useState<'all' | BotPermission>('all');
  const [sort, setSort] = useState<'name' | 'status' | 'recent'>('name');
  const [selectedName, setSelectedName] = useState('');
  const [draft, setDraft] = useState<Draft>({ response: '', aliases: '', cooldown: '30', permission: 'everyone' });
  const [newCommand, setNewCommand] = useState('');
  const [newResponse, setNewResponse] = useState('');
  const [newSound, setNewSound] = useState('');
  const [newCooldown, setNewCooldown] = useState('30');
  const [newPermission, setNewPermission] = useState<BotPermission>('everyone');
  const [newEnabled, setNewEnabled] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const next = await loadBotWithSounds();
      setBot(next.bot);
      setSounds(next.sounds);
      setSelectedName((current) => {
        if (current && next.bot.commands.some((command) => command.command === current)) return current;
        return next.bot.commands.find((command) => !archived(command))?.command ?? next.bot.commands[0]?.command ?? '';
      });
      setMessage('');
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Commands could not be loaded.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const selected = bot?.commands.find((command) => command.command === selectedName) ?? null;

  useEffect(() => {
    if (selected) setDraft(commandDraft(selected));
  }, [selectedName, selected?.command]);

  const visible = useMemo(() => {
    const query = search.trim().toLocaleLowerCase('en-GB');
    const statusRank = (command: BotCommand) =>
      archived(command) ? 3 : command.enabled ? 0 : command.ready && !unsupported(command) ? 1 : 2;

    return (bot?.commands ?? [])
      .filter((command) => {
        if (statusFilter === 'enabled' && (!command.enabled || archived(command))) return false;
        if (statusFilter === 'disabled' && (command.enabled || archived(command) || !command.ready || unsupported(command))) return false;
        if (statusFilter === 'setup' && (archived(command) || (command.ready && !unsupported(command)))) return false;
        if (statusFilter === 'archived' && !archived(command)) return false;
        if (statusFilter !== 'archived' && statusFilter !== 'all' && archived(command)) return false;
        if (statusFilter === 'all' && archived(command)) return false;
        if (permissionFilter !== 'all' && command.permission !== permissionFilter) return false;
        return !query || `!${command.command} ${command.aliases.join(' ')} ${command.responseTemplate ?? ''} ${permissionLabels[command.permission]}`.toLocaleLowerCase('en-GB').includes(query);
      })
      .sort((left, right) => {
        if (sort === 'recent') {
          const leftTime = left.lastTriggeredAt ? Date.parse(left.lastTriggeredAt) : 0;
          const rightTime = right.lastTriggeredAt ? Date.parse(right.lastTriggeredAt) : 0;
          if (leftTime !== rightTime) return rightTime - leftTime;
        } else if (sort === 'status') {
          const ranked = statusRank(left) - statusRank(right);
          if (ranked) return ranked;
        }
        return left.command.localeCompare(right.command, 'en-GB', { numeric: true });
      });
  }, [bot, permissionFilter, search, sort, statusFilter]);

  async function apply(operation: () => Promise<TwitchBotSnapshot>, success: string): Promise<boolean> {
    setBusy(true);
    setMessage('');
    try {
      const next = await operation();
      setBot(next);
      setMessage(success);
      return true;
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Command action failed.');
      return false;
    } finally {
      setBusy(false);
    }
  }

  async function create(event: FormEvent) {
    event.preventDefault();
    const cooldown = validCooldown(newCooldown);
    if (cooldown === null) {
      setMessage('Cooldown must be a whole number from 0 to 3600 seconds.');
      return;
    }
    const commandName = newCommand.replace(/^!/, '').trim().toLowerCase();
    const created = await apply(
      () => createBotCommand({
        command: newCommand,
        enabled: newEnabled,
        responseTemplate: newResponse.trim() || null,
        soundName: newSound || null,
        cooldownSeconds: cooldown,
        permission: newPermission,
      }),
      `!${commandName} created.`,
    );
    if (!created) return;
    setSelectedName(commandName);
    setNewCommand('');
    setNewResponse('');
    setNewSound('');
    setNewCooldown('30');
    setNewPermission('everyone');
    setNewEnabled(true);
  }

  async function saveSelected() {
    if (!selected) return;
    const cooldown = validCooldown(draft.cooldown);
    if (cooldown === null) {
      setMessage('Cooldown must be a whole number from 0 to 3600 seconds.');
      return;
    }
    const aliases = draft.aliases.split(',').map((item) => item.trim().replace(/^!/, '').toLowerCase()).filter(Boolean);
    await apply(
      () => updateBotCommand(selected.command, {
        responseTemplate: draft.response.trim() || null,
        aliases,
        cooldownSeconds: cooldown,
        permission: draft.permission,
      }),
      `!${selected.command} saved.`,
    );
  }

  async function toggleSelected() {
    if (!selected) return;
    await apply(() => updateBotCommand(selected.command, { enabled: !selected.enabled }), `!${selected.command} ${selected.enabled ? 'disabled' : 'enabled'}.`);
  }

  async function archiveSelected() {
    if (!selected) return;
    if (!window.confirm(`Archive !${selected.command}?\n\nIt will be disabled and hidden from the normal command list. You can show archived commands and restore it later.`)) return;
    await apply(() => archiveBotCommand(selected.command), `!${selected.command} archived.`);
  }

  async function restoreSelected() {
    if (!selected) return;
    await apply(() => restoreBotCommandForSetup(selected.command), `!${selected.command} restored for setup.`);
  }

  async function removeSelected() {
    if (!selected) return;
    if (!window.confirm(`Delete !${selected.command} permanently?\n\nThis is only available for commands created directly in FFSControl.`)) return;
    const name = selected.command;
    const removed = await apply(() => deleteBotCommand(name), `!${name} deleted.`);
    if (removed) setSelectedName('');
  }

  if (loading && !bot) return <div className="ffs-next-loading">Loading Commands…</div>;

  const createCooldown = validCooldown(newCooldown);
  const selectedCooldown = validCooldown(draft.cooldown);

  return (
    <div className="ffs-workspace-page">
      {message ? <div className="ffs-workspace-message">{message}</div> : null}
      {bot ? (
        <>
          <section className="ffs-workspace-hero">
            <div><small>CHAT COMMANDS</small><h2>{bot.commands.filter((command) => command.enabled).length.toLocaleString('en-GB')} enabled commands</h2><p>Create, find and edit commands without switching modes or opening hidden panels.</p></div>
            <button type="button" className="ffs-button secondary" onClick={() => void refresh()} disabled={busy}><RefreshCw size={15} /> Refresh</button>
          </section>

          <section className="ffs-workspace-card">
            <header><div><small>CREATE</small><h3>New command</h3></div><Plus size={18} /></header>
            <form className="ffs-command-create" onSubmit={(event) => void create(event)}>
              <label><span>Command</span><div className="ffs-prefix-input"><b>!</b><input value={newCommand} maxLength={50} placeholder="command" onChange={(event) => setNewCommand(event.target.value)} /></div></label>
              <label className="wide"><span>Chat response <small>optional if a sound is selected</small></span><input value={newResponse} maxLength={5000} placeholder="What FFSControl should say" onChange={(event) => setNewResponse(event.target.value)} /></label>
              <label><span>Sound <small>optional</small></span><select value={newSound} onChange={(event) => setNewSound(event.target.value)}><option value="">No sound</option>{sounds.map((sound) => <option key={sound.name} value={sound.name} disabled={!sound.enabled}>{sound.name}{sound.enabled ? '' : ' — disabled'}</option>)}</select></label>
              <label><span>Who can use it</span><select value={newPermission} onChange={(event) => setNewPermission(event.target.value as BotPermission)}>{Object.entries(permissionLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>
              <label><span>Cooldown</span><div className="ffs-suffix-input"><input type="number" min="0" max="3600" value={newCooldown} aria-invalid={createCooldown === null} onChange={(event) => setNewCooldown(event.target.value)} /><b>seconds</b></div></label>
              <label className="ffs-check-choice"><input type="checkbox" checked={newEnabled} onChange={(event) => setNewEnabled(event.target.checked)} /><span>Enable immediately</span></label>
              <button type="submit" className="ffs-button primary" disabled={busy || createCooldown === null || !newCommand.trim() || (!newResponse.trim() && !newSound)}>Create Command</button>
            </form>
          </section>

          <section className="ffs-command-workspace">
            <article className="ffs-workspace-card ffs-command-list-card">
              <header><div><small>FIND</small><h3>Commands</h3></div><span>{visible.length.toLocaleString('en-GB')}</span></header>
              <div className="ffs-command-list-toolbar">
                <label className="ffs-search-field"><Search size={15} /><input value={search} placeholder="Search commands, aliases or responses" onChange={(event) => setSearch(event.target.value)} /></label>
                <label><span>Status</span><select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value as typeof statusFilter)}><option value="all">Active list</option><option value="enabled">Enabled</option><option value="disabled">Disabled</option><option value="setup">Needs setup</option><option value="archived">Archived</option></select></label>
                <label><span>Permission</span><select value={permissionFilter} onChange={(event) => setPermissionFilter(event.target.value as typeof permissionFilter)}><option value="all">All permissions</option>{Object.entries(permissionLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>
                <label><span>Sort</span><select value={sort} onChange={(event) => setSort(event.target.value as typeof sort)}><option value="name">Name A–Z</option><option value="status">Status</option><option value="recent">Recently used</option></select></label>
              </div>
              <div className="ffs-command-list">
                {visible.map((command) => (
                  <button type="button" key={command.command} className={command.command === selectedName ? 'selected' : ''} onClick={() => setSelectedName(command.command)}>
                    <div><strong>!{command.command}</strong><span>{command.aliases.length ? `Aliases: ${command.aliases.map((alias) => `!${alias}`).join(', ')}` : permissionLabels[command.permission]}</span></div>
                    <small className={command.enabled ? 'good' : archived(command) ? 'muted' : command.ready ? '' : 'warning'}>{archived(command) ? 'ARCHIVED' : command.enabled ? 'ON' : command.ready ? 'OFF' : 'SETUP'}</small>
                  </button>
                ))}
                {visible.length === 0 ? <div className="ffs-empty-state">No commands match the current search and filters.</div> : null}
              </div>
            </article>

            <article className="ffs-workspace-card ffs-command-editor-card">
              {selected ? (
                <>
                  <header><div><small>EDIT</small><h3>!{selected.command}</h3></div><strong className={selected.enabled ? 'ffs-status good' : 'ffs-status'}>{archived(selected) ? 'ARCHIVED' : selected.enabled ? 'ENABLED' : 'DISABLED'}</strong></header>
                  {unsupported(selected) ? <div className="ffs-inline-warning">This command still needs an FFSControl implementation before it can be used safely.</div> : !selected.ready && !archived(selected) ? <div className="ffs-inline-warning"><strong>Needs setup before it can be enabled.</strong>{selected.readinessReasons.slice(0, 4).map((reason) => <span key={reason}>{friendlyReason(reason)}</span>)}</div> : null}
                  <div className="ffs-command-editor">
                    <label><span>Chat response</span><textarea rows={5} maxLength={5000} value={draft.response} onChange={(event) => setDraft((current) => ({ ...current, response: event.target.value }))} /></label>
                    <div className="ffs-form-two">
                      <label><span>Aliases <small>comma separated</small></span><input value={draft.aliases} onChange={(event) => setDraft((current) => ({ ...current, aliases: event.target.value }))} /></label>
                      <label><span>Who can use it</span><select value={draft.permission} onChange={(event) => setDraft((current) => ({ ...current, permission: event.target.value as BotPermission }))}>{Object.entries(permissionLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>
                      <label><span>Cooldown</span><div className="ffs-suffix-input"><input type="number" min="0" max="3600" value={draft.cooldown} aria-invalid={selectedCooldown === null} onChange={(event) => setDraft((current) => ({ ...current, cooldown: event.target.value }))} /><b>seconds</b></div></label>
                    </div>
                    {selected.actions.length ? <div className="ffs-command-actions"><span>Actions</span>{selected.actions.map((action, index) => <div key={`${action.type}-${index}`}><CheckCircle2 size={14} /> {actionText(action)}</div>)}</div> : null}
                    <div className="ffs-inline-actions"><button type="button" className="ffs-button primary" disabled={busy || archived(selected) || selectedCooldown === null} onClick={() => void saveSelected()}>Save Changes</button>{!archived(selected) ? <button type="button" className={`ffs-button ${selected.enabled ? 'warning' : 'secondary'}`} disabled={busy || (!selected.enabled && (!selected.ready || unsupported(selected)))} onClick={() => void toggleSelected()}>{selected.enabled ? 'Disable' : 'Enable'}</button> : null}</div>
                  </div>
                  <details className="ffs-disclosure"><summary>Command management</summary><div className="ffs-inline-actions">{archived(selected) && selected.internalState === 'retire_review' ? <button type="button" className="ffs-button secondary" disabled={busy} onClick={() => void restoreSelected()}><RotateCcw size={14} /> Restore for Setup</button> : !archived(selected) && selected.source !== 'user-created' ? <button type="button" className="ffs-button warning" disabled={busy} onClick={() => void archiveSelected()}><Archive size={14} /> Archive Command</button> : null}{selected.source === 'user-created' ? <button type="button" className="ffs-button danger" disabled={busy} onClick={() => void removeSelected()}><Trash2 size={14} /> Delete Command</button> : null}</div></details>
                </>
              ) : <div className="ffs-empty-state">Select a command to edit it.</div>}
            </article>
          </section>
        </>
      ) : null}
    </div>
  );
}
